import { appConfig } from '@/config/appConfig';

export interface GalleryItem {
  id: string;
  imageUrl: string;
  prompt: string;
  authorName: string;
  authorAvatar: string;
  toolType: 'text2img' | 'img2img' | 'edit' | 'restore' | 'tryon';
  styleName: string;
  ratio: string;
  likes: number;
  liked: boolean;
  views: number;
  createdAt: string;
  featured?: boolean;
}

const authorNameEn: Record<string, string> = {
  绘梦师小林: 'Dream Maker Lin',
  未来视界: 'Future Vision',
  艺术重构者: 'Art Remixer',
  插画师阿夏: 'Illustrator Asha',
  修图达人UU: 'Retouch Pro UU',
  时光修复师: 'Memory Restorer',
  时尚先锋: 'Fashion Pioneer',
  东瀛绘卷: 'Ukiyo-e Studio',
  春樱物语: 'Spring Sakura',
  风光猎人: 'Landscape Hunter',
  型男穿搭: 'Menswear Lab',
  深海绘者: 'Deep Sea Artist',
  玻璃匠人: 'Glass Artisan',
  记忆守护者: 'Memory Keeper',
  极光旅人: 'Aurora Traveler',
  潮牌试衣间: 'Streetwear Fitting Room',
};

const styleNameEn: Record<string, string> = {
  文生图: 'Text to Image',
  图生图: 'Image to Image',
  修图改图: 'Edit Image',
  画质修复: 'HD Restore',
  虚拟试衣: 'Model Try-On',
};

const createdAtEn: Record<string, string> = {
  '2小时前': '2h ago',
  '4小时前': '4h ago',
  '5小时前': '5h ago',
  '6小时前': '6h ago',
  '8小时前': '8h ago',
  '10小时前': '10h ago',
  '12小时前': '12h ago',
  '14小时前': '14h ago',
  '16小时前': '16h ago',
  '18小时前': '18h ago',
  '20小时前': '20h ago',
  '1天前': '1d ago',
  '2天前': '2d ago',
  '3天前': '3d ago',
};

const promptEn: Record<string, string> = {
  gal_001: 'Mystical fantasy forest with glowing fireflies, moss-covered ancient ruins, moonlight through the canopy, cinematic composition, digital painting style.',
  gal_002: 'Cyberpunk city street at night, neon signs reflected on wet pavement, flying vehicles weaving between skyscrapers.',
  gal_003: 'Transform a reference portrait into a classical oil painting with thick brushstrokes, Renaissance texture, and warm amber tones.',
  gal_004: 'Cozy cottage interior with a warm fireplace, book-filled shelves, a sleeping cat, afternoon sunlight, and watercolor illustration style.',
  gal_005: 'Add golden butterfly hair accessories, sparkling particles, a dreamy atmosphere, and soft pink-and-gold color grading.',
  gal_006: 'Restore a 1950s vintage family photo with AI colorization, sharper faces, nostalgic warmth, and high-definition detail.',
  gal_007: 'Virtual try-on: elegant young woman wearing a flowing crimson evening gown with crystal embellishments, luxury fashion photography.',
  gal_008: 'Reimagine modern architecture as a Japanese ukiyo-e woodblock print with blooming cherry blossoms, Mt. Fuji, bold outlines, and flat colors.',
  gal_009: 'Replace the background with floating cherry blossom petals while preserving studio lighting and a clean professional cutout.',
  gal_010: 'Restore a mountain lake sunrise landscape with snow-peak reflections, enhanced dynamic range, and crisp visible details.',
  gal_011: 'Virtual try-on: stylish young man wearing a tailored navy pinstripe business suit with a modern slim fit.',
  gal_012: 'Mystical underwater world with glowing jellyfish in the deep blue ocean, sun rays through the water, coral reef, and fantasy digital art.',
  gal_013: 'Transform a portrait into colorful stained-glass mosaic art with jewel tones, geometric fragments, and a glowing backlit effect.',
  gal_014: 'Restore and colorize a 1960s black-and-white historical family photo with natural colors and enhanced sharpness.',
  gal_015: 'Replace the background with spectacular aurora lights over a calm lake, with vivid green and purple reflections.',
  gal_016: 'Virtual try-on: trendy young woman wearing an oversized pastel hoodie and cargo pants, dynamic playful streetwear pose.',
};

const stableGalleryImageUrls: Record<string, string | undefined> = {
  gal_001: appConfig.generateResultsText2Img[0]?.imageUrl,
  gal_002: appConfig.generateResultsText2Img[1]?.imageUrl,
  gal_003: appConfig.generateResultsImg2Img[0]?.imageUrl,
  gal_004: appConfig.generateResultsText2Img[2]?.imageUrl,
  gal_005: appConfig.generateResultsEdit[0]?.imageUrl,
  gal_006: appConfig.generateResultsRestore[2]?.imageUrl,
  gal_007: appConfig.generateResultsTryon[0]?.imageUrl,
  gal_008: appConfig.generateResultsImg2Img[1]?.imageUrl,
  gal_009: appConfig.generateResultsEdit[2]?.imageUrl,
  gal_010: appConfig.generateResultsRestore[1]?.imageUrl,
  gal_011: appConfig.generateResultsTryon[1]?.imageUrl,
  gal_012: appConfig.generateResultsText2Img[0]?.imageUrl,
  gal_013: appConfig.generateResultsImg2Img[2]?.imageUrl,
  gal_014: appConfig.generateResultsRestore[2]?.imageUrl,
  gal_015: appConfig.generateResultsEdit[1]?.imageUrl,
  gal_016: appConfig.generateResultsTryon[2]?.imageUrl,
};

export function localizeGalleryItem(item: GalleryItem, language?: string): GalleryItem {
  if (!language?.toLowerCase().startsWith('en')) return item;
  return {
    ...item,
    prompt: promptEn[item.id] ?? item.prompt,
    authorName: authorNameEn[item.authorName] ?? item.authorName,
    styleName: styleNameEn[item.styleName] ?? item.styleName,
    createdAt: createdAtEn[item.createdAt] ?? item.createdAt,
  };
}

const galleryMockDataSeed: GalleryItem[] = [
  {
    id: 'gal_001',
    imageUrl: 'https://readdy.ai/api/search-image?query=ethereal%20fantasy%20forest%20scene%20with%20glowing%20fireflies%20and%20ancient%20stone%20ruins%20covered%20in%20moss%2C%20magical%20atmosphere%2C%20soft%20moonlight%20filtering%20through%20ancient%20trees%2C%20cinematic%20composition%2C%20rich%20saturated%20colors%2C%20digital%20painting%20art%20style%2C%20high%20resolution%20artwork%2C%20clean%20simple%20background&width=600&height=800&seq=gal1&orientation=portrait',
    prompt: '神秘梦幻森林，萤火虫飞舞，古老遗迹覆盖青苔，月光透过树冠，电影级构图，数字绘画风格',
    authorName: '绘梦师小林',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20cute%20cartoon%20character%20avatar%2C%20soft%20pink%20and%20purple%20gradient%20background%2C%20round%20friendly%20face%2C%20simple%20geometric%20style%2C%20cheerful%20expression%2C%20clean%20simple%20background&width=80&height=80&seq=gava1&orientation=squarish',
    toolType: 'text2img',
    styleName: '文生图',
    ratio: '3:4',
    likes: 1248,
    liked: false,
    views: 8932,
    createdAt: '2小时前',
    featured: true,
  },
  {
    id: 'gal_002',
    imageUrl: 'https://readdy.ai/api/search-image?query=futuristic%20cyberpunk%20city%20street%20at%20night%20with%20neon%20signs%20in%20japanese%20and%20english%20reflecting%20on%20wet%20pavement%2C%20flying%20vehicles%20between%20towering%20skyscrapers%2C%20rain%20atmosphere%2C%20highly%20detailed%20digital%20art%2C%20cinematic%20lighting%2C%20blade%20runner%20aesthetic%2C%20clean%20simple%20background&width=600&height=800&seq=gal2&orientation=portrait',
    prompt: '赛博朋克城市夜景，霓虹灯招牌倒映在湿漉漉的地面，飞行车辆穿梭于摩天大楼之间',
    authorName: '未来视界',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20cool%20avatar%20illustration%2C%20dark%20blue%20and%20cyan%20gradient%20background%2C%20abstract%20geometric%20face%2C%20modern%20digital%20art%20style%2C%20mysterious%20vibe%2C%20clean%20simple%20background&width=80&height=80&seq=gava2&orientation=squarish',
    toolType: 'text2img',
    styleName: '文生图',
    ratio: '3:4',
    likes: 987,
    liked: true,
    views: 6541,
    createdAt: '4小时前',
    featured: true,
  },
  {
    id: 'gal_003',
    imageUrl: 'https://readdy.ai/api/search-image?query=portrait%20photo%20transformed%20into%20classical%20oil%20painting%20style%20with%20visible%20thick%20brush%20strokes%2C%20rich%20impasto%20texture%20on%20canvas%2C%20renaissance%20art%20style%2C%20warm%20amber%20and%20golden%20tones%2C%20museum%20quality%20artwork%2C%20dramatic%20chiaroscuro%20lighting%2C%20female%20subject%2C%20clean%20simple%20background&width=600&height=600&seq=gal3&orientation=squarish',
    prompt: '将参考照片转换为古典油画风格，厚涂笔触，文艺复兴艺术质感，暖琥珀色调',
    authorName: '艺术重构者',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20artistic%20avatar%2C%20warm%20orange%20and%20gold%20gradient%20background%2C%20painter%20palette%20icon%20shape%2C%20creative%20artistic%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava3&orientation=squarish',
    toolType: 'img2img',
    styleName: '图生图',
    ratio: '1:1',
    likes: 2156,
    liked: false,
    views: 12890,
    createdAt: '5小时前',
    featured: true,
  },
  {
    id: 'gal_004',
    imageUrl: 'https://readdy.ai/api/search-image?query=cozy%20cottage%20interior%20with%20warm%20crackling%20fireplace%2C%20wooden%20bookshelves%20filled%20with%20colorful%20books%2C%20fluffy%20cat%20sleeping%20on%20vintage%20armchair%2C%20afternoon%20golden%20sunlight%20streaming%20through%20lace%20curtains%2C%20watercolor%20illustration%20style%2C%20soft%20pastel%20colors%2C%20storybook%20atmosphere%2C%20clean%20simple%20background&width=600&height=450&seq=gal4&orientation=landscape',
    prompt: '温馨小屋室内，壁炉温暖，书架林立，猫咪睡在扶手椅上，午后阳光洒入窗户，水彩插画风格',
    authorName: '插画师阿夏',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20cute%20avatar%20illustration%2C%20soft%20mint%20green%20and%20cream%20gradient%20background%2C%20simple%20cat%20face%20outline%2C%20kawaii%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava4&orientation=squarish',
    toolType: 'text2img',
    styleName: '文生图',
    ratio: '4:3',
    likes: 756,
    liked: false,
    views: 4321,
    createdAt: '6小时前',
  },
  {
    id: 'gal_005',
    imageUrl: 'https://readdy.ai/api/search-image?query=young%20woman%20portrait%20with%20magical%20golden%20butterfly%20accessories%20floating%20in%20hair%2C%20sparkling%20particle%20effects%2C%20enhanced%20dreamy%20atmosphere%2C%20soft%20pink%20and%20gold%20color%20grading%2C%20editorial%20beauty%20photography%2C%20high%20end%20retouching%2C%20clean%20simple%20background&width=600&height=800&seq=gal5&orientation=portrait',
    prompt: '在头发上添加金色蝴蝶配饰，闪光粒子特效，增强梦幻氛围，柔粉与金色色调',
    authorName: '修图达人UU',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20stylish%20avatar%2C%20soft%20lavender%20and%20pink%20gradient%20background%2C%20simple%20flower%20motif%2C%20elegant%20feminine%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava5&orientation=squarish',
    toolType: 'edit',
    styleName: '修图改图',
    ratio: '3:4',
    likes: 1567,
    liked: false,
    views: 9876,
    createdAt: '8小时前',
    featured: true,
  },
  {
    id: 'gal_006',
    imageUrl: 'https://readdy.ai/api/search-image?query=ultra%20sharp%20restored%20vintage%20photograph%20from%201950s%2C%20colorized%20and%20enhanced%20with%20modern%20AI%20technology%2C%20sharp%20facial%20details%2C%20warm%20nostalgic%20sepia%20tones%20transitioning%20to%20natural%20colors%2C%20old%20photo%20restoration%20result%2C%20professional%20photo%20recovery%2C%20memories%20preserved%20in%20stunning%20high%20quality%2C%20family%20portrait%2C%20clean%20simple%20background&width=600&height=800&seq=gal6&orientation=portrait',
    prompt: '1950年代老照片修复：AI上色增强，清晰面部细节，温暖怀旧色调，珍贵记忆高清重现',
    authorName: '时光修复师',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20vintage%20style%20avatar%2C%20sepia%20and%20brown%20gradient%20background%2C%20old%20camera%20silhouette%2C%20nostalgic%20retro%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava6&orientation=squarish',
    toolType: 'restore',
    styleName: '画质修复',
    ratio: '3:4',
    likes: 3421,
    liked: true,
    views: 21560,
    createdAt: '10小时前',
    featured: true,
  },
  {
    id: 'gal_007',
    imageUrl: 'https://readdy.ai/api/search-image?query=full%20body%20fashion%20photo%20of%20elegant%20young%20asian%20woman%20wearing%20flowing%20crimson%20red%20evening%20gown%20with%20crystal%20embellishments%2C%20luxury%20fashion%20photography%2C%20professional%20studio%20lighting%2C%20confident%20graceful%20pose%2C%20high%20end%20fashion%20editorial%20style%2C%20clean%20white%20background%2C%20clean%20simple%20background&width=600&height=1067&seq=gal7&orientation=portrait',
    prompt: '虚拟试衣：优雅年轻女性身着飘逸深红色晚礼服，水晶装饰点缀，高级时尚摄影',
    authorName: '时尚先锋',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20fashion%20avatar%2C%20black%20and%20white%20gradient%20background%2C%20high%20heel%20shoe%20silhouette%2C%20chic%20modern%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava7&orientation=squarish',
    toolType: 'tryon',
    styleName: '虚拟试衣',
    ratio: '9:16',
    likes: 892,
    liked: false,
    views: 5432,
    createdAt: '12小时前',
  },
  {
    id: 'gal_008',
    imageUrl: 'https://readdy.ai/api/search-image?query=modern%20architecture%20building%20reimagined%20in%20traditional%20japanese%20ukiyo-e%20woodblock%20print%20style%2C%20cherry%20blossoms%20in%20full%20bloom%2C%20mt%20fuji%20in%20distant%20background%2C%20bold%20outlines%2C%20flat%20color%20areas%2C%20traditional%20japanese%20art%20aesthetic%2C%20clean%20simple%20background&width=600&height=600&seq=gal8&orientation=squarish',
    prompt: '现代建筑重新想象为日本浮世绘木版画风格，樱花盛开，富士山远景，粗线条平涂色块',
    authorName: '东瀛绘卷',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20japanese%20style%20avatar%2C%20soft%20red%20and%20white%20gradient%20background%2C%20simple%20cherry%20blossom%20motif%2C%20traditional%20eastern%20aesthetic%2C%20clean%20simple%20background&width=80&height=80&seq=gava8&orientation=squarish',
    toolType: 'img2img',
    styleName: '图生图',
    ratio: '1:1',
    likes: 1103,
    liked: false,
    views: 6789,
    createdAt: '14小时前',
  },
  {
    id: 'gal_009',
    imageUrl: 'https://readdy.ai/api/search-image?query=portrait%20with%20removed%20background%20replaced%20by%20floating%20pink%20cherry%20blossom%20petals%20in%20spring%20breeze%2C%20studio%20lighting%20preserved%20on%20subject%2C%20clean%20professional%20extraction%2C%20dreamy%20spring%20atmosphere%2C%20professional%20photo%20editing%2C%20high%20quality%20retouching%2C%20clean%20simple%20background&width=600&height=800&seq=gal9&orientation=portrait',
    prompt: '移除背景替换为飘落樱花花瓣，保留影棚布光，干净抠图，春日梦幻氛围',
    authorName: '春樱物语',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20spring%20themed%20avatar%2C%20soft%20pink%20and%20white%20gradient%20background%2C%20delicate%20cherry%20blossom%20branch%2C%20romantic%20gentle%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava9&orientation=squarish',
    toolType: 'edit',
    styleName: '修图改图',
    ratio: '3:4',
    likes: 623,
    liked: false,
    views: 3890,
    createdAt: '16小时前',
  },
  {
    id: 'gal_010',
    imageUrl: 'https://readdy.ai/api/search-image?query=ultra%20sharp%20landscape%20photograph%20of%20pristine%20alpine%20mountain%20lake%20at%20sunrise%2C%20crystal%20clear%20water%20reflection%20of%20snow%20peaks%2C%20enhanced%20dynamic%20range%2C%20vivid%20saturated%20colors%2C%20every%20leaf%20and%20rock%20visible%20in%20foreground%2C%20HDR%20quality%20professional%20restoration%2C%20clean%20simple%20background&width=600&height=450&seq=gal10&orientation=landscape',
    prompt: '风景画质修复：高山湖泊日出美景，雪峰倒影，增强动态范围，每处细节清晰可见',
    authorName: '风光猎人',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20nature%20avatar%2C%20soft%20blue%20and%20green%20gradient%20background%2C%20simple%20mountain%20outline%2C%20outdoor%20adventure%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava10&orientation=squarish',
    toolType: 'restore',
    styleName: '画质修复',
    ratio: '4:3',
    likes: 445,
    liked: false,
    views: 2678,
    createdAt: '18小时前',
  },
  {
    id: 'gal_011',
    imageUrl: 'https://readdy.ai/api/search-image?query=full%20body%20photo%20of%20stylish%20young%20professional%20man%20in%20tailored%20navy%20blue%20business%20suit%20with%20subtle%20pinstripes%2C%20modern%20slim%20fit%2C%20confident%20corporate%20portrait%20pose%2C%20clean%20studio%20background%2C%20high%20quality%20fashion%20photography%2C%20professional%20attire%20showcase%2C%20clean%20simple%20background&width=600&height=1067&seq=gal11&orientation=portrait',
    prompt: '虚拟试衣：时尚年轻男性身着定制海军蓝细条纹商务西装，现代修身剪裁',
    authorName: '型男穿搭',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20masculine%20avatar%2C%20dark%20navy%20and%20gray%20gradient%20background%2C%20simple%20suit%20tie%20silhouette%2C%20professional%20business%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava11&orientation=squarish',
    toolType: 'tryon',
    styleName: '虚拟试衣',
    ratio: '9:16',
    likes: 567,
    liked: false,
    views: 3456,
    createdAt: '20小时前',
  },
  {
    id: 'gal_012',
    imageUrl: 'https://readdy.ai/api/search-image?query=mystical%20underwater%20scene%20with%20bioluminescent%20jellyfish%20floating%20in%20deep%20blue%20ocean%2C%20rays%20of%20sunlight%20penetrating%20water%20surface%2C%20coral%20reef%20in%20background%2C%20ethereal%20glow%20effects%2C%20fantasy%20digital%20art%20style%2C%20rich%20deep%20blues%20and%20teals%2C%20clean%20simple%20background&width=600&height=800&seq=gal12&orientation=portrait',
    prompt: '神秘海底世界，发光水母在深蓝海洋中漂浮，阳光穿透水面，珊瑚礁背景，梦幻数字艺术',
    authorName: '深海绘者',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20ocean%20themed%20avatar%2C%20deep%20blue%20and%20teal%20gradient%20background%2C%20simple%20jellyfish%20silhouette%2C%20mysterious%20underwater%20vibe%2C%20clean%20simple%20background&width=80&height=80&seq=gava12&orientation=squarish',
    toolType: 'text2img',
    styleName: '文生图',
    ratio: '3:4',
    likes: 1789,
    liked: true,
    views: 11234,
    createdAt: '1天前',
    featured: true,
  },
  {
    id: 'gal_013',
    imageUrl: 'https://readdy.ai/api/search-image?query=portrait%20transformed%20into%20colorful%20stained%20glass%20mosaic%20art%20style%2C%20vibrant%20jewel%20tones%20of%20ruby%20red%20and%20emerald%20green%2C%20geometric%20fragmented%20shapes%20forming%20facial%20features%2C%20backlit%20glowing%20halo%20effect%2C%20medieval%20cathedral%20aesthetic%2C%20intricate%20decorative%20details%2C%20clean%20simple%20background&width=600&height=800&seq=gal13&orientation=portrait',
    prompt: '人像转换为彩色玻璃马赛克艺术风格，宝石色调，几何碎片形状，背光发光效果',
    authorName: '玻璃匠人',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20artistic%20avatar%2C%20multicolor%20jewel%20tone%20gradient%20background%2C%20simple%20stained%20glass%20pattern%20fragment%2C%20creative%20craft%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava13&orientation=squarish',
    toolType: 'img2img',
    styleName: '图生图',
    ratio: '3:4',
    likes: 834,
    liked: false,
    views: 5234,
    createdAt: '1天前',
  },
  {
    id: 'gal_014',
    imageUrl: 'https://readdy.ai/api/search-image?query=restored%20black%20and%20white%20historical%20photograph%20from%201960s%2C%20AI%20colorized%20with%20realistic%20natural%20colors%2C%20enhanced%20sharpness%20and%20clarity%2C%20sharp%20facial%20details%20and%20clothing%20textures%2C%20warm%20nostalgic%20tones%2C%20professional%20photo%20restoration%2C%20family%20gathering%20scene%2C%20clean%20simple%20background&width=600&height=450&seq=gal14&orientation=landscape',
    prompt: '1960年代黑白历史照片AI上色修复，增强清晰度，真实自然色彩，家庭聚会场景',
    authorName: '记忆守护者',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20vintage%20photo%20avatar%2C%20warm%20brown%20and%20cream%20gradient%20background%2C%20simple%20photo%20frame%20silhouette%2C%20nostalgic%20memory%20keeper%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava14&orientation=squarish',
    toolType: 'restore',
    styleName: '画质修复',
    ratio: '4:3',
    likes: 2901,
    liked: false,
    views: 18765,
    createdAt: '2天前',
    featured: true,
  },
  {
    id: 'gal_015',
    imageUrl: 'https://readdy.ai/api/search-image?query=cityscape%20photo%20with%20background%20changed%20to%20spectacular%20aurora%20borealis%20northern%20lights%20over%20calm%20lake%2C%20vivid%20green%20and%20purple%20lights%20reflection%20on%20water%20surface%2C%20enhanced%20magical%20atmosphere%2C%20professional%20photo%20manipulation%2C%20seamless%20blending%20between%20foreground%20and%20new%20sky%2C%20clean%20simple%20background&width=600&height=600&seq=gal15&orientation=squarish',
    prompt: '将背景替换为壮观极光夜空，湖面倒映绿紫色光芒，魔幻氛围，专业照片合成',
    authorName: '极光旅人',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20aurora%20avatar%2C%20vivid%20green%20and%20purple%20gradient%20background%2C%20simple%20northern%20lights%20wave%20shape%2C%20travel%20adventure%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava15&orientation=squarish',
    toolType: 'edit',
    styleName: '修图改图',
    ratio: '1:1',
    likes: 1203,
    liked: true,
    views: 7890,
    createdAt: '2天前',
  },
  {
    id: 'gal_016',
    imageUrl: 'https://readdy.ai/api/search-image?query=full%20body%20fashion%20photo%20of%20trendy%20young%20woman%20wearing%20oversized%20streetwear%20hoodie%20in%20pastel%20colors%20and%20cargo%20pants%2C%20urban%20style%20with%20sneakers%2C%20dynamic%20playful%20pose%2C%20modern%20street%20fashion%20photography%2C%20gradient%20studio%20background%2C%20youthful%20energetic%20vibe%2C%20clean%20simple%20background&width=600&height=1067&seq=gal16&orientation=portrait',
    prompt: '虚拟试衣：潮流年轻女性身着oversize马卡龙色连帽衫和工装裤，动感俏皮姿态',
    authorName: '潮牌试衣间',
    authorAvatar: 'https://readdy.ai/api/search-image?query=minimalist%20streetwear%20avatar%2C%20pastel%20pink%20and%20yellow%20gradient%20background%2C%20simple%20hoodie%20silhouette%2C%20trendy%20urban%20style%2C%20clean%20simple%20background&width=80&height=80&seq=gava16&orientation=squarish',
    toolType: 'tryon',
    styleName: '虚拟试衣',
    ratio: '9:16',
    likes: 678,
    liked: false,
    views: 4567,
    createdAt: '3天前',
  },
];

export const galleryMockData: GalleryItem[] = galleryMockDataSeed.map((item) => ({
  ...item,
  imageUrl: stableGalleryImageUrls[item.id] ?? item.imageUrl,
}));

export const toolTypeFilters = [
  { id: 'all', labelKey: 'gallery_filter_all', color: '#FF6B9D' },
  { id: 'text2img', labelKey: 'gallery_filter_text2img', color: '#FF6B9D' },
  { id: 'img2img', labelKey: 'gallery_filter_img2img', color: '#F59E0B' },
  { id: 'edit', labelKey: 'gallery_filter_edit', color: '#10B981' },
  { id: 'restore', labelKey: 'gallery_filter_restore', color: '#6366F1' },
  { id: 'tryon', labelKey: 'gallery_filter_tryon', color: '#EC4899' },
];
